'use client';
import { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  FormControl,
  InputLabel,
  Select as MuiSelect,
  MenuItem,
  OutlinedInput,
  Button,
  CircularProgress,
  Stack,
  LinearProgress,
  Checkbox,
  ListItemText,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Divider,
  Pagination,
  Tabs,
  Tab
} from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import StorageIcon from '@mui/icons-material/Storage';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ExcelJS from 'exceljs';
import JSZip from 'jszip';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import dayjs from 'dayjs';

import { useMasterVehicleList } from '../../stores/masterVehicleStore';
import MasterVehicleAddEdit from '../master-data/vehicle/MasterVehicleAddEdit';
import MasterCompanyAddEdit from '../master-data/company/MasterCompanyAddEdit';
import { useCompanyDataList } from '../../stores/companyStore';
import { useNotifier } from '../../components/Notifier';
import PickerField from '../../components/PickerField';
import usePickerPagination from '../../hooks/usePickerPagination';
import { useLanguage } from '../../utils/i18n';
import { timezone } from '../../utils/timeZone';
import { fetchVehicleEvidence } from '../../stores/reportStore';
import DataTable from '../../components/DataTable';

const getCompanyIdFromStorage = () => {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      const stored = localStorage.getItem('companyId');
      if (stored && String(stored).toLowerCase() !== 'all') {
        return String(stored);
      }
    }
  } catch (err) {
    console.warn('cannot read localStorage', err);
  }
  return '';
};

const sanitizeFilename = (str) =>
  String(str || '')
    .replace(/[\\/:*?"<>|]/g, '_')
    .trim();

const getUserNameFromStorage = () => {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      const rawUser = localStorage.getItem('user');
      if (rawUser) {
        try {
          const parsed = JSON.parse(rawUser);
          if (parsed && typeof parsed === 'object') {
            const name = parsed.name || parsed.username || parsed.email;
            if (name) return String(name);
          }
        } catch {}
      }
      const directName = localStorage.getItem('name') || localStorage.getItem('username');
      if (directName) return String(directName);
    }
  } catch (err) {
    console.warn('cannot read localStorage for user', err);
  }
  return 'User';
};

const buildVehicleWorkbook = ({ licensePlate, vehicleRecords, eventTypesList, getEventLabel, companyName, dateRangeStr, userName }) => {
  const workbook = new ExcelJS.Workbook();
  workbook.creator = 'OSLOG';
  workbook.created = new Date();
  const exportTimeStr = dayjs().format('DD/MM/YYYY HH:mm:ss');

  eventTypesList.forEach((ev) => {
    const evRows = vehicleRecords.filter((r) => r.event_type === ev);
    const evLabel = getEventLabel(ev);
    const sheetTitle = evLabel.slice(0, 31);
    const worksheet = workbook.addWorksheet(sheetTitle);

    const isHarsh = ['harsh_braking', 'harsh_acceleration', 'harsh_cornering'].includes(ev);
    const isOverSpeed = ev === 'over_speed';
    const isUnderSpeed = ev === 'under_speed';
    const isIdle = ev === 'idle_time';

    let columns = [{ header: 'License Plate', key: 'license_plate', width: 18 }];

    if (isHarsh) {
      columns.push({ header: 'Date Time', key: 'date_time', width: 22 });
    } else {
      columns.push({ header: 'Start Time', key: 'start_time', width: 22 });
    }

    if (isIdle) {
      columns.push({ header: 'Duration', key: 'duration', width: 16 });
    } else {
      columns.push({ header: 'Speed (km/h)', key: 'speed', width: 16 });
    }

    if (isOverSpeed) {
      columns.push({ header: 'Max Speed (km/h)', key: 'max_speed', width: 18 });
    }
    if (isUnderSpeed) {
      columns.push({ header: 'Min Speed (km/h)', key: 'min_speed', width: 18 });
    }

    columns.push(
      { header: 'RPM', key: 'rpm', width: 14 },
      { header: 'Location', key: 'location', width: 45 },
      { header: 'Coordinates', key: 'coordinates', width: 25 }
    );

    worksheet.columns = columns.map((c) => ({ key: c.key, width: c.width }));

    // Metadata Header Rows
    const metaRow1 = worksheet.addRow(['Company Name', companyName || '-']);
    metaRow1.getCell(1).font = { bold: true };
    metaRow1.getCell(2).font = { bold: true };

    const metaRow2 = worksheet.addRow(['Event Type', evLabel || ev || '-']);
    metaRow2.getCell(1).font = { bold: true };
    metaRow2.getCell(2).font = { bold: true };

    const metaRow3 = worksheet.addRow(['Total Data', `${evRows.length} Data`]);
    metaRow3.getCell(1).font = { bold: true };
    metaRow3.getCell(2).font = { bold: true };

    const metaRow4 = worksheet.addRow(['Date Range', dateRangeStr || '-']);
    metaRow4.getCell(1).font = { bold: true };

    const metaRow5 = worksheet.addRow(['Export By', `(${exportTimeStr}) ${userName}`]);
    metaRow5.getCell(1).font = { bold: true };

    worksheet.addRow([]); // Blank spacing row (Row 6)

    // Table Header Row (Row 7)
    const headerRow = worksheet.addRow(columns.map((c) => c.header));
    headerRow.font = { bold: true, color: { argb: 'FFFFFFFF' } };
    headerRow.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FF1976D2' }
    };
    headerRow.alignment = { vertical: 'middle', horizontal: 'center' };

    evRows.forEach((row) => {
      const rowData = {
        license_plate: row?.vehicle?.license_plate || row?.vehicle?.name || row?.license_plate || licensePlate || '-'
      };

      if (isHarsh) {
        rowData.date_time = row?.start_time ? dayjs(row.start_time).format('DD/MM/YYYY HH:mm:ss') : '-';
      } else {
        rowData.start_time = row?.start_time ? dayjs(row.start_time).format('DD/MM/YYYY HH:mm:ss') : '-';
      }

      if (isIdle) {
        rowData.duration = row?.duration ?? row?.total_duration ?? '-';
      } else {
        rowData.speed = row?.speed != null ? row.speed : '-';
      }

      if (isOverSpeed) {
        rowData.max_speed = row?.max_speed != null ? row.max_speed : row?.speed != null ? row.speed : '-';
      }
      if (isUnderSpeed) {
        rowData.min_speed = row?.min_speed != null ? row.min_speed : '-';
      }

      rowData.rpm = row?.engine_rpm ?? '-';
      rowData.location = row?.location || row?.address || '-';
      rowData.coordinates = row?.lat && row?.lon ? `${row.lat}, ${row.lon}` : '-';

      worksheet.addRow(rowData);
    });

    worksheet.views = [{ state: 'frozen', xSplit: 0, ySplit: 7 }];
  });

  return workbook;
};

export default function ReportSpeedByRitase() {
  const { t } = useLanguage();
  const notify = useNotifier();

  const [loading, setLoading] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [loadingText, setLoadingText] = useState('');
  const [generationTimeRange, setGenerationTimeRange] = useState('');

  // Grouped vehicle data
  const [vehicleDataMap, setVehicleDataMap] = useState({});
  const [generatedVehicleIds, setGeneratedVehicleIds] = useState([]);
  const [expandedAccordion, setExpandedAccordion] = useState(false);
  const [accordionPage, setAccordionPage] = useState(1);
  const accordionItemsPerPage = 5;
  const [activeEventTab, setActiveEventTab] = useState({});

  // Filters State
  const [periodType, setPeriodType] = useState('today');

  const EVENT_OPTIONS = useMemo(
    () => [
      { value: 'over_speed', label: t('reports.overSpeed', 'Over Speed') },
      { value: 'under_speed', label: t('reports.underSpeed', 'Under Speed') },
      { value: 'harsh_braking', label: t('reports.harshBraking', 'Harsh Braking') },
      { value: 'harsh_acceleration', label: t('reports.harshAcceleration', 'Harsh Acceleration') },
      { value: 'harsh_cornering', label: t('reports.harshCornering', 'Harsh Cornering') },
      { value: 'idle_time', label: t('reports.engineIdle', 'Idle Time') }
    ],
    [t]
  );

  const getEventLabel = (val) => EVENT_OPTIONS.find((o) => o.value === val)?.label || val;

  const [selectedEventTypes, setSelectedEventTypes] = useState([]);

  const isAllEventsSelected = EVENT_OPTIONS.length > 0 && selectedEventTypes.length === EVENT_OPTIONS.length;

  const handleEventTypeChange = (event) => {
    const { value } = event.target;
    const values = typeof value === 'string' ? value.split(',') : value;
    if (values.includes('all') || values.includes('select-all')) {
      if (selectedEventTypes.length === EVENT_OPTIONS.length) {
        setSelectedEventTypes([]);
      } else {
        setSelectedEventTypes(EVENT_OPTIONS.map((e) => e.value));
      }
    } else {
      setSelectedEventTypes(values);
    }
  };

  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [dateError, setDateError] = useState('');

  useEffect(() => {
    if (!periodType) return;
    if (periodType === 'custom' && fromDate && toDate) return;

    const now = dayjs();
    if (periodType === 'custom') {
      if (!fromDate) {
        setFromDate('');
        setToDate('');
      }
      return;
    }

    let from;
    let to = now;

    if (periodType === 'today') {
      from = now.startOf('day');
    } else if (periodType === 'last_week') {
      from = now.subtract(7, 'day').startOf('day');
      to = now.endOf('day');
    } else if (periodType === 'last_month') {
      from = now.subtract(1, 'month').startOf('day');
      to = now.endOf('day');
    } else {
      return;
    }

    if (from && to) {
      setFromDate(from.format('YYYY-MM-DD HH:mm:ss'));
      setToDate(to.format('YYYY-MM-DD HH:mm:ss'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periodType]);

  const [selectedCompanyIds, setSelectedCompanyIds] = useState(() => {
    const defaultId = getCompanyIdFromStorage();
    return defaultId ? [defaultId] : [];
  });
  const [selectedVehicles, setSelectedVehicles] = useState([]);
  const selectedVehicleIds = useMemo(() => selectedVehicles.map((v) => String(v.value)), [selectedVehicles]);

  const companyPickerParams = usePickerPagination({
    fetchHook: useCompanyDataList,
    buildArgs: (state) => [state.page, state.pageSize, state.searchKey, state.searchValue],
    searchKey: 'name'
  });
  const companyOptions = companyPickerParams.options;

  const selectedCompaniesParam = useMemo(() => {
    const arr = Array.isArray(selectedCompanyIds) ? selectedCompanyIds : [selectedCompanyIds].filter(Boolean);
    if (arr.length === 0 || arr.includes('all')) {
      return companyOptions.map((c) => ({ value: c.value }));
    }
    return arr.map((id) => ({ value: id }));
  }, [selectedCompanyIds, companyOptions]);

  const vehiclePickerParams = usePickerPagination({
    fetchHook: useMasterVehicleList,
    buildArgs: (state) => [state.page, state.pageSize, selectedCompaniesParam, state.searchKey, state.searchValue],
    formatOption: (p) => ({
      value: p.id,
      label: p.license_plate,
      original: p
    }),
    searchKey: 'license_plate',
    companyId: selectedCompaniesParam
  });
  const vehicleList = vehiclePickerParams.options;

  const vehicleOptionsNormalized = useMemo(
    () =>
      (vehicleList || []).map((c) => ({
        value: String(c.id ?? c.vehicle_id ?? c.value ?? c),
        label: c.original?.license_plate ?? c.license_plate ?? c.label ?? c.name ?? String(c.id ?? ''),
        companyName:
          c.original?.join?.company_name ||
          c.original?.company_name ||
          c.join?.company_name ||
          c.company_name ||
          c.companyName ||
          t('reportModule.unknownCompany', 'Unknown Company'),
        raw: c.raw ?? c
      })),
    [vehicleList, t]
  );

  const validateDateRange = (start, end) => {
    if (!start || !end) return false;
    const diffMinutes = end.diff(start, 'minute');
    if (diffMinutes > 31 * 24 * 60) {
      setDateError(t('reportModule.errMaxRange31Days', 'Maximum time span of 31 days'));
      return false;
    } else if (diffMinutes < 0) {
      setDateError(t('reports.errStartTimeLater', 'The start time cannot exceed the end time'));
      return false;
    }
    setDateError('');
    return true;
  };

  const handleFetchData = async () => {
    const arr = Array.isArray(selectedCompanyIds) ? selectedCompanyIds : [selectedCompanyIds].filter(Boolean);
    const cId = arr[0] || '';

    if (selectedCompanyIds.length === 0) {
      notify({ message: t('reports.errSelectCompany', 'Please select a company'), severity: 'warning' });
      return;
    }
    if (selectedVehicleIds.length === 0) {
      notify({ message: t('reports.errSelectVehicle', 'Please select a vehicle'), severity: 'warning' });
      return;
    }
    if (selectedEventTypes.length === 0) {
      notify({ message: t('reportModule.errSelectEvent', 'Please select at least one event'), severity: 'warning' });
      return;
    }
    if (!fromDate || !toDate || dateError) {
      notify({ message: t('reports.errSelectValidDateRange', 'Please select a valid date range'), severity: 'warning' });
      return;
    }

    setLoading(true);
    setLoadingProgress(0);
    setLoadingText(t('reportModule.checkingData', 'Checking data...'));
    setGenerationTimeRange(`${dayjs(fromDate).format('DD/MM/YYYY HH:mm')} - ${dayjs(toDate).format('DD/MM/YYYY HH:mm')}`);

    const initMap = {};
    selectedVehicleIds.forEach((id) => {
      initMap[id] = { data: [], total: 0, loading: true };
    });
    setVehicleDataMap(initMap);
    setGeneratedVehicleIds([...selectedVehicleIds]);
    setAccordionPage(1);

    if (selectedVehicleIds.length === 1) {
      setExpandedAccordion(selectedVehicleIds[0]);
    } else {
      setExpandedAccordion(false);
    }

    try {
      const localStart = dayjs(fromDate);
      const localEnd = dayjs(toDate);

      let apiChunks = [];
      const isFullDayPeriod = ['last_week', 'last_month'].includes(periodType);

      if (isFullDayPeriod || periodType === 'today') {
        let curr = localStart.startOf('day');
        const end = localEnd.startOf('day');
        while (curr.isBefore(end) || curr.isSame(end, 'day')) {
          apiChunks.push({
            startLocal: curr.startOf('day'),
            endLocal: curr.endOf('day'),
            dateStr: curr.format('DD/MM/YYYY')
          });
          curr = curr.add(1, 'day');
        }
      } else {
        let curr = localStart.startOf('day');
        const end = localEnd.startOf('day');
        while (curr.isBefore(end) || curr.isSame(end, 'day')) {
          let chunkStart = curr.startOf('day');
          let chunkEnd = curr.endOf('day');
          if (curr.isSame(localStart, 'day')) chunkStart = localStart;
          if (curr.isSame(localEnd, 'day')) chunkEnd = localEnd;
          apiChunks.push({
            startLocal: chunkStart,
            endLocal: chunkEnd,
            dateStr: curr.format('DD/MM/YYYY')
          });
          curr = curr.add(1, 'day');
        }
      }

      const eventsToFetch = selectedEventTypes;
      const totalVehicles = selectedVehicleIds.length;
      const totalSteps = totalVehicles * apiChunks.length * eventsToFetch.length;
      let stepCount = 0;

      for (let vIdx = 0; vIdx < selectedVehicleIds.length; vIdx++) {
        const vId = selectedVehicleIds[vIdx];
        const vInfo =
          selectedVehicles.find((v) => String(v.value) === String(vId)) ||
          vehicleOptionsNormalized.find((v) => String(v.value) === String(vId));
        const licensePlate = vInfo?.label || String(vId);

        let vehicleRecords = [];

        for (let cIdx = 0; cIdx < apiChunks.length; cIdx++) {
          const tz = typeof timezone === 'function' ? timezone() : Number(timezone) || 7;
          const chunk = apiChunks[cIdx];
          const dateDisplay = chunk.dateStr || chunk.startLocal.format('DD/MM/YYYY');
          const chunkStart = chunk.startLocal.subtract(tz, 'hour').format('YYYY-MM-DDTHH:mm:ss[Z]');
          const chunkEnd = chunk.endLocal.subtract(tz, 'hour').format('YYYY-MM-DDTHH:mm:ss[Z]');

          for (let evIdx = 0; evIdx < eventsToFetch.length; evIdx++) {
            const ev = eventsToFetch[evIdx];
            const evLabel = EVENT_OPTIONS.find((o) => o.value === ev)?.label || ev;

            setLoadingText(
              `${t('reportModule.processingUnit', 'Processing unit:')} ${licensePlate} (${vIdx + 1}/${totalVehicles}) - ${evLabel} - ${dateDisplay}`
            );

            let offset = 0;
            const limit = 1000;
            let hasMore = true;

            while (hasMore) {
              try {
                const res = await fetchVehicleEvidence(cId, vId, ev, chunkStart, chunkEnd, limit, offset, tz);

                if (res && res.data) {
                  const rawItems = Array.isArray(res.data) ? res.data : res.data.data || [];

                  const recordsTotal =
                    res.recordsTotal ??
                    res.totalRecord ??
                    res.data?.recordsTotal ??
                    res.data?.totalRecord ??
                    res.data?.total ??
                    rawItems.length;

                  const tagged = rawItems.map((item) => ({
                    ...item,
                    event_type: item.event_type || ev,
                    vehicle: item.vehicle || { license_plate: licensePlate, name: licensePlate }
                  }));

                  vehicleRecords = vehicleRecords.concat(tagged);
                  offset += limit;

                  if (offset >= recordsTotal || rawItems.length === 0) {
                    hasMore = false;
                  }
                } else {
                  hasMore = false;
                }
              } catch (err) {
                console.error(`Error fetching for vehicle ${vId} event ${ev} on ${chunkStart}`, err);
                hasMore = false;
              }
            }

            stepCount++;
            setLoadingProgress(Math.min(99, Math.round((stepCount / totalSteps) * 100)));
          }
        }

        vehicleRecords.sort((a, b) => new Date(b.start_time || 0).getTime() - new Date(a.start_time || 0).getTime());

        setVehicleDataMap((prev) => ({
          ...prev,
          [vId]: {
            data: vehicleRecords,
            total: vehicleRecords.length,
            loading: false
          }
        }));
      }

      setLoadingProgress(100);
      notify({ message: t('reports.dataFetchedSuccess', 'Data fetched successfully'), severity: 'success' });
    } catch (error) {
      console.error(error);
      notify({ message: t('reports.errFetchData', 'Failed to fetch data'), severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleResetFilter = () => {
    setSelectedEventTypes([]);
    setSelectedVehicles([]);
    setVehicleDataMap({});
    setGeneratedVehicleIds([]);
    setPeriodType('today');
    setGenerationTimeRange('');
    setDateError('');
  };

  const handleExportVehicle = async (vId, licensePlate) => {
    const vData = vehicleDataMap[vId];
    if (!vData || !vData.data || vData.data.length === 0) {
      notify({ message: t('reportModule.noDataToExport', 'Tidak ada data untuk diekspor'), severity: 'warning' });
      return;
    }

    const vInfo =
      selectedVehicles.find((o) => String(o.value) === String(vId)) ||
      vehicleOptionsNormalized.find((o) => String(o.value) === String(vId));
    const compObj = companyOptions.find((c) => String(c.value) === String(selectedCompanyIds[0]));
    const vehicleCompanyName =
      vInfo?.companyName && vInfo.companyName !== t('reportModule.unknownCompany', 'Unknown Company')
        ? vInfo.companyName
        : compObj?.label || 'Company';
    const compName = sanitizeFilename(vehicleCompanyName);
    const cleanPlate = sanitizeFilename(licensePlate || vId);
    const dateStr = dayjs().format('YYYYMMDD');
    const dateRangeStr = `${dayjs(fromDate).format('DD/MM/YYYY HH:mm:ss')} - ${dayjs(toDate).format('DD/MM/YYYY HH:mm:ss')}`;
    const userName = getUserNameFromStorage();

    const workbook = buildVehicleWorkbook({
      licensePlate,
      vehicleRecords: vData.data,
      eventTypesList: selectedEventTypes,
      getEventLabel,
      companyName: vehicleCompanyName,
      dateRangeStr,
      userName
    });
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = window.URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `${cleanPlate}_ReportSpeedByRitase_${compName}_${dateStr}.xlsx`;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    window.URL.revokeObjectURL(url);
  };

  const handleExportAll = async () => {
    const vIdsWithData = generatedVehicleIds.filter((vId) => vehicleDataMap[vId]?.data?.length > 0);
    if (vIdsWithData.length === 0) {
      notify({ message: t('reportModule.noAllDataExport', 'There is no aggregate data to export'), severity: 'warning' });
      return;
    }

    const compObj = companyOptions.find((c) => String(c.value) === String(selectedCompanyIds[0]));
    const fallbackCompName = compObj?.label || 'Company';
    const compName = sanitizeFilename(fallbackCompName);
    const dateStr = dayjs().format('YYYYMMDD');
    const dateRangeStr = `${dayjs(fromDate).format('DD/MM/YYYY HH:mm:ss')} - ${dayjs(toDate).format('DD/MM/YYYY HH:mm:ss')}`;
    const userName = getUserNameFromStorage();

    notify({ message: t('reportModule.preparingExportAll', 'Preparing for the export of complete vehicles...'), severity: 'info' });

    const zip = new JSZip();

    for (const vId of vIdsWithData) {
      const vInfo =
        selectedVehicles.find((o) => String(o.value) === String(vId)) ||
        vehicleOptionsNormalized.find((o) => String(o.value) === String(vId));
      const licensePlate = vInfo?.label || String(vId);
      const cleanPlate = sanitizeFilename(licensePlate);
      const vehicleCompanyName =
        vInfo?.companyName && vInfo.companyName !== t('reportModule.unknownCompany', 'Unknown Company')
          ? vInfo.companyName
          : fallbackCompName;
      const vehicleCompName = sanitizeFilename(vehicleCompanyName);

      const workbook = buildVehicleWorkbook({
        licensePlate,
        vehicleRecords: vehicleDataMap[vId].data,
        eventTypesList: selectedEventTypes,
        getEventLabel,
        companyName: vehicleCompanyName,
        dateRangeStr,
        userName
      });
      const buffer = await workbook.xlsx.writeBuffer();
      const excelFilename = `${cleanPlate}_ReportSpeedByRitase_${vehicleCompName}_${dateStr}.xlsx`;
      zip.file(excelFilename, buffer);
    }

    const zipBlob = await zip.generateAsync({ type: 'blob', compression: 'STORE' });
    const url = window.URL.createObjectURL(zipBlob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `ReportSpeedByRitase_${compName}_${dateStr}.zip`;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    window.URL.revokeObjectURL(url);

    notify({ message: t('reportModule.exportAllSuccess', 'Export all successful!'), severity: 'success' });
  };

  const getColumnsForEvent = (eventType) => {
    const isHarsh = ['harsh_braking', 'harsh_acceleration', 'harsh_cornering'].includes(eventType);
    const isOverSpeed = eventType === 'over_speed';
    const isUnderSpeed = eventType === 'under_speed';
    const isIdle = eventType === 'idle_time';

    const cols = [
      {
        field: 'license_plate',
        headerName: t('reports.licensePlate', 'License Plate'),
        width: 140,
        valueGetter: (val, params) => params?.vehicle?.license_plate || params?.vehicle?.name || params?.license_plate || '-'
      }
    ];

    if (isHarsh) {
      cols.push({
        field: 'start_time',
        headerName: t('reportModule.dateTime', 'Date Time'),
        width: 180,
        valueGetter: (val, params) => (params?.start_time ? dayjs(params.start_time).format('DD/MM/YYYY HH:mm:ss') : '-')
      });
    } else {
      cols.push({
        field: 'start_time',
        headerName: t('achievement.startTime', 'Start Time'),
        width: 180,
        valueGetter: (val, params) => (params?.start_time ? dayjs(params.start_time).format('DD/MM/YYYY HH:mm:ss') : '-')
      });
    }

    if (isIdle) {
      cols.push({
        field: 'duration',
        headerName: t('reports.duration', 'Duration'),
        width: 130,
        valueGetter: (val, params) => params?.duration ?? params?.total_duration ?? '-'
      });
    } else {
      cols.push({
        field: 'speed',
        headerName: t('dashboardAsset.speed', 'Speed (km/h)'),
        width: 130,
        valueGetter: (val, params) => (params?.speed != null ? `${params.speed} km/h` : '-')
      });
    }

    if (isOverSpeed) {
      cols.push({
        field: 'max_speed',
        headerName: t('masterJetty.maxSpeed', 'Max Speed (km/h)'),
        width: 160,
        valueGetter: (val, params) =>
          params?.max_speed != null ? `${params.max_speed} km/h` : params?.speed != null ? `${params.speed} km/h` : '-'
      });
    }
    if (isUnderSpeed) {
      cols.push({
        field: 'min_speed',
        headerName: t('masterVehicle.minSpeed', 'Min Speed (km/h)'),
        width: 160,
        valueGetter: (val, params) =>
          params?.min_speed != null ? `${params.min_speed} km/h` : params?.speed != null ? `${params.speed} km/h` : '-'
      });
    }

    cols.push(
      {
        field: 'engine_rpm',
        headerName: t('exportWaypoint.rpm', 'RPM'),
        width: 110,
        valueGetter: (val, params) => (params?.engine_rpm != null && params.engine_rpm !== '' ? `${params.engine_rpm} rpm` : '-')
      },
      {
        field: 'location',
        headerName: t('notification.location', 'Location'),
        flex: 1,
        minWidth: 220,
        valueGetter: (val, params) => params?.location || params?.address || '-'
      },
      {
        field: 'coordinates',
        headerName: t('exportWaypoint.coordinate', 'Coordinate'),
        width: 160,
        renderCell: (params) => {
          const lat = params.row?.lat;
          const lon = params.row?.lon;
          if (!lat || !lon) return '-';
          return (
            <a
              href={`http://maps.google.com/maps?z=18&q=${lat},${lon}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: '#1976d2', textDecoration: 'none', fontWeight: 500 }}
              onClick={(e) => e.stopPropagation()}
            >
              {`${lat}, ${lon}`}
            </a>
          );
        }
      }
    );

    return cols;
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1, minHeight: 0 }}>
        <Box sx={{ mb: 1, flexShrink: 0 }}>
          <Typography variant="h4" fontWeight={700} display="flex" alignItems="center" gap={1}>
            <StorageIcon fontSize="large" color="primary" />
            {t('reportModule.speedByRitaseReport', 'Speed By Ritase Report')}
          </Typography>
        </Box>

        <Paper
          elevation={0}
          sx={{
            p: 1.5,
            mb: 1,
            flexShrink: 0,
            borderRadius: 3,
            border: '1px solid',
            borderColor: 'divider',
            background: 'linear-gradient(to right bottom, #ffffff, #fcfcfc)'
          }}
        >
          <Box display="flex" flexWrap="wrap" gap={2.5} alignItems="center">
            <Box sx={{ flex: '1 1 200px' }}>
              <PickerField
                label={t('general.company', 'Company')}
                value={selectedCompanyIds}
                {...companyPickerParams}
                options={companyOptions}
                onChange={(ids) => {
                  setSelectedCompanyIds(ids ? (Array.isArray(ids) ? ids : [ids]) : []);
                  setSelectedVehicles([]);
                }}
                disabled={loading}
                allowAdd
                AddComponent={MasterCompanyAddEdit}
                dialogTitle={t('general.selectCompany', 'Select Company')}
                multiSelect={false}
              />
            </Box>
            <Box sx={{ flex: '1 1 250px' }}>
              <PickerField
                label={t('general.selectVehicle', 'Select Vehicle') + ' *'}
                value={selectedVehicleIds}
                {...vehiclePickerParams}
                options={vehicleOptionsNormalized}
                onChange={(ids) => {
                  if (!ids || ids.length === 0) {
                    setSelectedVehicles([]);
                    return;
                  }
                  const newSelected = ids.map((id) => {
                    const strId = String(id);
                    let match = vehicleOptionsNormalized.find((v) => String(v.value) === strId);
                    if (!match) match = selectedVehicles.find((v) => String(v.value) === strId);

                    if (!match) {
                      const rawMatch =
                        (vehicleList || []).find((v) => String(v.value) === strId) ||
                        (vehiclePickerParams.listOptions || []).find((v) => String(v.value) === strId);
                      if (rawMatch) {
                        match = {
                          value: strId,
                          label: rawMatch.raw?.license_plate ?? rawMatch.original?.license_plate ?? rawMatch.label ?? strId,
                          companyName:
                            rawMatch.original?.join?.company_name ||
                            rawMatch.original?.company_name ||
                            rawMatch.join?.company_name ||
                            rawMatch.company_name ||
                            rawMatch.companyName ||
                            t('reportModule.unknownCompany', 'Unknown Company'),
                          raw: rawMatch.raw
                        };
                      } else {
                        match = { value: strId, label: strId };
                      }
                    }
                    return match;
                  });
                  setSelectedVehicles(newSelected);
                }}
                disabled={loading}
                dialogTitle={t('general.selectVehicle', 'Select Vehicle')}
                multiSelect={true}
                allowAdd
                AddComponent={MasterVehicleAddEdit}
              />
            </Box>
            <Box sx={{ flex: '0 1 220px' }}>
              <FormControl fullWidth size="medium">
                <InputLabel id="event-type-label">{t('reportModule.eventType', 'Event Type')}</InputLabel>
                <MuiSelect
                  labelId="event-type-label"
                  multiple
                  value={selectedEventTypes}
                  onChange={handleEventTypeChange}
                  disabled={loading}
                  input={<OutlinedInput label={t('reportModule.eventType', 'Event Type')} />}
                  renderValue={(selected) => {
                    if (selected.length === 0) {
                      return <em>{t('reports.selectEvent', 'Select Event')}</em>;
                    }
                    if (selected.length === EVENT_OPTIONS.length) {
                      return t('reportModule.allEvents', 'All Events');
                    }
                    return selected.map((val) => EVENT_OPTIONS.find((o) => o.value === val)?.label || val).join(', ');
                  }}
                  sx={{ bgcolor: 'background.paper' }}
                >
                  <MenuItem value="all">
                    <Checkbox
                      checked={isAllEventsSelected}
                      indeterminate={selectedEventTypes.length > 0 && selectedEventTypes.length < EVENT_OPTIONS.length}
                    />
                    <ListItemText primary={t('reportModule.allEvents', 'All Events')} />
                  </MenuItem>
                  {EVENT_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      <Checkbox checked={selectedEventTypes.indexOf(opt.value) > -1} />
                      <ListItemText primary={opt.label} />
                    </MenuItem>
                  ))}
                </MuiSelect>
              </FormControl>
            </Box>

            <Box sx={{ flex: '0 1 180px' }}>
              <FormControl fullWidth size="medium">
                <InputLabel id="period-label">{t('reports.selectPeriod', 'Pilih Periode')}</InputLabel>
                <MuiSelect
                  labelId="period-label"
                  value={periodType}
                  onChange={(e) => setPeriodType(e.target.value)}
                  disabled={loading}
                  input={<OutlinedInput label={t('reports.selectPeriod', 'Pilih Periode')} />}
                  sx={{ bgcolor: 'background.paper' }}
                >
                  <MenuItem value="today">{t('mapsTracking.today', 'Today')}</MenuItem>
                  <MenuItem value="last_week">{t('time.lastWeek', 'Last Week')}</MenuItem>
                  <MenuItem value="last_month">{t('time.lastMonth', 'Last Month')}</MenuItem>
                  <MenuItem value="custom">{t('reports.custom', 'Custom')}</MenuItem>
                </MuiSelect>
              </FormControl>
            </Box>

            {periodType === 'custom' && (
              <Box sx={{ flex: '1 1 350px' }}>
                <Stack direction="row" spacing={2}>
                  <DateTimePicker
                    views={['year', 'month', 'day', 'hours', 'minutes']}
                    ampm={false}
                    label={t('reports.from', 'From')}
                    timeSteps={{ minutes: 1 }}
                    format="DD/MM/YYYY HH:mm"
                    value={fromDate ? dayjs(fromDate) : null}
                    onChange={(v) => {
                      const newV = v ? v.second(0).format('YYYY-MM-DD HH:mm:ss') : '';
                      setFromDate(newV);
                      validateDateRange(dayjs(newV), dayjs(toDate));
                    }}
                    maxDate={toDate ? dayjs(toDate) : undefined}
                    disabled={loading}
                    slotProps={{ textField: { size: 'small', fullWidth: true, error: !!dateError, sx: { bgcolor: 'background.paper' } } }}
                  />
                  <DateTimePicker
                    views={['year', 'month', 'day', 'hours', 'minutes']}
                    ampm={false}
                    label={t('reports.to', 'To')}
                    timeSteps={{ minutes: 1 }}
                    format="DD/MM/YYYY HH:mm"
                    value={toDate ? dayjs(toDate) : null}
                    onChange={(v) => {
                      const newV = v ? v.second(59).format('YYYY-MM-DD HH:mm:ss') : '';
                      setToDate(newV);
                      validateDateRange(dayjs(fromDate), dayjs(newV));
                    }}
                    minDate={fromDate ? dayjs(fromDate) : undefined}
                    maxDate={fromDate ? dayjs(fromDate).add(31, 'day') : undefined}
                    disabled={loading}
                    slotProps={{ textField: { size: 'small', fullWidth: true, error: !!dateError, sx: { bgcolor: 'background.paper' } } }}
                  />
                </Stack>
              </Box>
            )}

            <Box sx={{ flex: '0 0 auto', ml: 'auto', display: 'flex', gap: 1 }}>
              <Button variant="outlined" color="secondary" onClick={handleResetFilter} disabled={loading} sx={{ px: 2, minWidth: 0 }}>
                <RestartAltIcon />
              </Button>
              <Button
                variant="contained"
                onClick={handleFetchData}
                disabled={loading || !!dateError || selectedVehicleIds.length === 0 || selectedEventTypes.length === 0}
                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SearchIcon />}
                sx={{
                  height: 40,
                  px: 2,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 'bold',
                  boxShadow: 'none'
                }}
              >
                {loading ? t('revenue.searching', 'Searching...') : t('general.search', 'Search')}
              </Button>
              <Button
                variant="outlined"
                color="primary"
                onClick={handleExportAll}
                disabled={loading || generatedVehicleIds.length === 0 || Object.keys(vehicleDataMap).length === 0}
                startIcon={<FileDownloadIcon />}
                sx={{
                  height: 40,
                  px: 2,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 'bold'
                }}
              >
                {t('reportModule.exportAll', 'Export All')}
              </Button>
            </Box>
          </Box>

          {dateError && (
            <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
              {dateError}
            </Typography>
          )}
        </Paper>

        {generationTimeRange && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              mb: 1.5,
              px: 2,
              py: 1.5,
              borderRadius: 2,
              bgcolor: 'rgba(72, 93, 154, 0.05)',
              border: '1px solid rgba(72, 93, 154, 0.2)',
              boxShadow: '0 2px 8px rgba(72, 93, 154, 0.05)'
            }}
          >
            <InfoOutlinedIcon sx={{ color: '#485D9A' }} />
            <Typography variant="body2" sx={{ color: '#333', fontWeight: 500 }}>
              {t('revenue.showingDataFrom', 'Showing data from')}{' '}
              <Box component="span" sx={{ color: '#485D9A', fontWeight: 'bold' }}>
                {dayjs(fromDate).format('DD MMMM YYYY HH:mm:ss')}
              </Box>{' '}
              {t('revenue.to', 'to')}{' '}
              <Box component="span" sx={{ color: '#485D9A', fontWeight: 'bold' }}>
                {dayjs(toDate).format('DD MMMM YYYY HH:mm:ss')}
              </Box>
            </Typography>
          </Box>
        )}

        {loading && loadingProgress > 0 && (
          <Box
            sx={{
              width: '100%',
              mb: 2,
              p: 2.5,
              textAlign: 'center',
              bgcolor: '#f8fafc',
              borderRadius: 2,
              border: '1px dashed rgba(72, 93, 154, 0.4)'
            }}
          >
            <Box sx={{ width: '60%', mx: 'auto' }}>
              <LinearProgress
                variant="determinate"
                value={loadingProgress}
                sx={{
                  height: 10,
                  borderRadius: 5,
                  mb: 1.5,
                  bgcolor: 'rgba(72, 93, 154, 0.2)',
                  '& .MuiLinearProgress-bar': { bgcolor: '#485D9A' }
                }}
              />
              <Typography variant="body2" color="text.secondary" fontWeight="bold">
                {loadingProgress}% - {loadingText}
              </Typography>
            </Box>
          </Box>
        )}

        {/* Empty State */}
        {generatedVehicleIds.length === 0 && !loading && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              py: 10,
              px: 3,
              bgcolor: '#ffffff',
              borderRadius: 2,
              border: '1px dashed',
              borderColor: 'rgba(72, 93, 154, 0.3)',
              boxShadow: '0 4px 24px rgba(72, 93, 154, 0.04)',
              mt: 1
            }}
          >
            <Box
              sx={{
                bgcolor: 'rgba(72, 93, 154, 0.05)',
                p: 2.5,
                borderRadius: '50%',
                mb: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <StorageIcon sx={{ fontSize: 56, color: '#b6b6b8', opacity: 0.8 }} />
            </Box>
            <Typography variant="h6" color="#333" fontWeight="700" gutterBottom>
              {t('reportModule.noDataToDisplay', 'No Data to Display')}
            </Typography>
            <Typography variant="body2" color="text.secondary" textAlign="center" maxWidth="sm" sx={{ lineHeight: 1.6 }}>
              {t(
                'reportModule.pleaseSelectTheCompanyVehicleAnd',
                'Please select the company, vehicle, and time period from the filter above, then click'
              )}{' '}
              <b>{t('dashboardFuel.generate', 'Search')}</b> {t('reportModule.toViewTheDetailedSensorReport', 'to view the report.')}
            </Typography>
          </Box>
        )}

        {/* Accordions per Vehicle */}
        {generatedVehicleIds.length > 0 &&
          generatedVehicleIds.slice((accordionPage - 1) * accordionItemsPerPage, accordionPage * accordionItemsPerPage).map((vId) => {
            const vInfo =
              selectedVehicles.find((o) => String(o.value) === String(vId)) ||
              vehicleOptionsNormalized.find((o) => String(o.value) === String(vId));
            const vData = vehicleDataMap[vId];

            if (!vData) return null;

            return (
              <Accordion
                key={vId}
                expanded={expandedAccordion === vId}
                onChange={(e, isExpanded) => setExpandedAccordion(isExpanded ? vId : false)}
                sx={{
                  mb: 1.5,
                  borderRadius: '12px !important',
                  '&:before': { display: 'none' },
                  border: '1px solid',
                  borderColor: expandedAccordion === vId ? 'primary.main' : 'divider',
                  boxShadow: expandedAccordion === vId ? '0 4px 12px rgba(0,0,0,0.08)' : 'none',
                  transition: 'all 0.2s ease-in-out'
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon color={expandedAccordion === vId ? 'primary' : 'action'} />}
                  sx={{
                    bgcolor: expandedAccordion === vId ? 'rgba(25, 118, 210, 0.04)' : 'background.paper',
                    px: 3,
                    minHeight: 64
                  }}
                >
                  <Box sx={{ display: 'flex', width: '100%', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', gap: 2 }}>
                      <Typography fontWeight="800" variant="h6" color={expandedAccordion === vId ? 'primary.main' : 'text.primary'}>
                        {vInfo?.label || vId}
                      </Typography>
                      <Chip
                        label={vData.loading ? t('general.loading', 'Memuat...') : `${vData.total} ${t('reportModule.records', 'Records')}`}
                        color={vData.total > 0 ? 'primary' : 'default'}
                        variant={expandedAccordion === vId ? 'filled' : 'outlined'}
                        size="small"
                        sx={{ fontWeight: 'bold' }}
                      />
                    </Box>
                    <Box display="flex" justifyContent="flex-end">
                      <Button
                        variant="outlined"
                        color="primary"
                        size="small"
                        startIcon={<FileDownloadIcon />}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleExportVehicle(vId, vInfo?.label || vId);
                        }}
                        disabled={vData.loading || vData.total === 0}
                        sx={{ bgcolor: 'background.paper', '&:hover': { bgcolor: 'primary.50' } }}
                      >
                        {t('revenue.exportExcel', 'Export Excel')}
                      </Button>
                    </Box>
                  </Box>
                </AccordionSummary>
                <Divider />
                <AccordionDetails sx={{ p: 0, bgcolor: '#fbfbfb' }}>
                  <Box sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: '#ffffff', px: 2 }}>
                    <Tabs
                      value={activeEventTab[vId] || (selectedEventTypes[0] ?? '')}
                      onChange={(e, val) => setActiveEventTab((prev) => ({ ...prev, [vId]: val }))}
                      variant="scrollable"
                      scrollButtons="auto"
                      sx={{
                        minHeight: 48,
                        '& .MuiTab-root': {
                          textTransform: 'none',
                          fontWeight: 600,
                          fontSize: '0.875rem',
                          minHeight: 48
                        }
                      }}
                    >
                      {selectedEventTypes.map((evType) => {
                        const count = (vData.data || []).filter((r) => r.event_type === evType).length;
                        const label = getEventLabel(evType);
                        return (
                          <Tab
                            key={evType}
                            value={evType}
                            label={
                              <Box display="flex" alignItems="center" gap={1}>
                                <span>{label}</span>
                                <Chip
                                  size="small"
                                  label={count}
                                  color={count > 0 ? 'primary' : 'default'}
                                  sx={{ height: 20, fontSize: '0.75rem', fontWeight: 'bold' }}
                                />
                              </Box>
                            }
                          />
                        );
                      })}
                    </Tabs>
                  </Box>
                  <Box sx={{ p: 2 }}>
                    {(() => {
                      const currentType = activeEventTab[vId] || selectedEventTypes[0];
                      const typeRows = (vData.data || []).filter((r) => r.event_type === currentType);
                      const typeCols = getColumnsForEvent(currentType);

                      return (
                        <DataTable
                          title={`${vInfo?.label || vId} - ${getEventLabel(currentType)}`}
                          columns={typeCols}
                          rows={typeRows.map((r, i) => ({ ...r, id: r.id || `${vId}_${currentType}_${i}` }))}
                          loading={vData.loading}
                          pagination
                          paginationMode="client"
                          pageSizeOptions={[10, 25, 50, 100]}
                          hideAddButton={true}
                          hideExportButton={true}
                          hideSearchField={false}
                        />
                      );
                    })()}
                  </Box>
                </AccordionDetails>
              </Accordion>
            );
          })}

        {/* Pagination Controls for Accordions */}
        {generatedVehicleIds.length > accordionItemsPerPage && (
          <Box display="flex" justifyContent="center" mt={3} mb={2}>
            <Pagination
              count={Math.ceil(generatedVehicleIds.length / accordionItemsPerPage)}
              page={accordionPage}
              onChange={(e, val) => {
                setAccordionPage(val);
                setExpandedAccordion(false);
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
              color="primary"
              size="large"
              showFirstButton
              showLastButton
            />
          </Box>
        )}
      </Box>
    </LocalizationProvider>
  );
}
